import { NO, UIBaseButton, UIColor, UITextView, UIView, YES } from "uicore-ts"
import {
    RHProcurementOffer,
    RHProcurementPart,
    RHProcurementPartOffer,
    RHProcurementPublic
} from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import { RHFeatureFlags } from "../../Core/RHFeatureFlags"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHAccordionView } from "../../Custom components/RH/RHAccordionView"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHNotificationBadge } from "../../Custom components/RH/RHNotificationBadge"
import { RHColors, RHFont, RHRadius } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHMakeOfferViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHProcurementPartDisplay } from "../../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHProcurementPartView } from "../RHProcurementPartView"
import { OfferMessageThreadView } from "./OfferMessageThreadView"
import { OfferPartCardView } from "./OfferPartCardView"
import { OfferSubmittedView } from "./OfferSubmittedView"
import { RHProcurementPartOfferGrouping } from "./RHProcurementPartOfferGrouping"


// ─────────────────────────────────────────────────────────────────────────────
// One read-only card per submitted part offer.
//
// Reuses the shared OfferPartCardView (part header + price + documents) and adds
// the result-specific footer: a single consolidated decision banner, the
// evaluator's note, and the private 1:1 messaging accordion.
// ─────────────────────────────────────────────────────────────────────────────

class OfferPartResultCard extends OfferPartCardView {

    readonly decisionBanner = new RHAlertBannerView()

    // Shown instead of decisionBanner while a part continues in a further
    // round this company wasn't invited to (see
    // liveReconciliationRoundProcurementIdForCurrentCompany).
    readonly reconciliationPendingBanner = new RHAlertBannerView()

    readonly evaluationNotesLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            style: { fontSize: "14px", fontStyle: "italic" }
        })

    readonly _messageThreadView = new OfferMessageThreadView()

    readonly _messagingAccordion = new RHAccordionView()

    _procurementOfferId?: string
    _partId?: string

    override _focusManager: RHFocusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        for (const fileListView of [this.requiredDocumentsListView, this.documentsListView]) {
            if (fileListView.superview === this) {
                fileListView.forEachViewInSubtree(view => {
                    if (view instanceof UIBaseButton) {
                        items.push(view)
                    }
                })
            }
        }
        if (this._messagingAccordion.superview === this) {
            items.push(this._messagingAccordion.headerButton)
            if (this._messagingAccordion.expanded) {
                items.push(this._messageThreadView.inputArea, this._messageThreadView.sendButton)
            }
        }
        return items
    })

    constructor(readonly numberOfRootProcurementParts: number) {
        super()
        if (RHFeatureFlags.offerMessagingIsVisible) {
            this._messagingAccordion.contentView = this._messageThreadView
        }
    }

    refreshMessageBadge() {
        if (!RHFeatureFlags.offerMessagingIsVisible) {
            this._clearPrivateMessagingUI()
            return
        }
        if (!this._procurementOfferId) {
            return
        }
        const unreadCount = RHCore.sharedInstance.notificationStore.offerMessageCount(
            this._procurementOfferId
        )
        RHNotificationBadge.applyUnreadEmphasis(
            this._messagingAccordion.headerButton.titleLabel,
            unreadCount > 0
        )
    }

    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        if (!RHFeatureFlags.offerMessagingIsVisible || !this._procurementOfferId) {
            this._clearPrivateMessagingUI()
            return
        }
        // Repaint the accordion's unread emphasis on every count change affecting
        // this offer. This is the path that clears the emphasis after
        // opening the accordion (MarkOfferMessagesAsRead triggers the store's
        // reloadOfferMessageCounts) — the thread-push subscription below only
        // enables it. Read-only handler: never invalidates the same store.
        RHCore.sharedInstance.notificationStore.subscribe(this, (affectedIds) => {
            const affectsThisOffer =
                affectedIds.length === 0 ||
                (this._procurementOfferId !== undefined && affectedIds.includes(this._procurementOfferId))
            if (affectsThisOffer) {
                this.refreshMessageBadge()
            }
        })
        if (RHFeatureFlags.offerMessagingIsVisible && this._procurementOfferId) {
            RHCore.sharedInstance.offerStore.subscribeToThread(
                this,
                this._procurementOfferId,
                () => {
                    RHNotificationBadge.applyUnreadEmphasis(
                        this._messagingAccordion.headerButton.titleLabel,
                        YES
                    )
                }
            )
        }
    }

    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        RHCore.sharedInstance.offerStore.unsubscribeFromThread(this)
        RHCore.sharedInstance.notificationStore.unsubscribe(this)
    }

    private _clearPrivateMessagingUI() {
        RHCore.sharedInstance.offerStore.unsubscribeFromThread(this)
        RHNotificationBadge.applyUnreadEmphasis(this._messagingAccordion.headerButton.titleLabel, NO)
        this._messagingAccordion.expanded = NO
        this._messagingAccordion.contentView = undefined
        this._messagingAccordion.removeFromSuperview()
        this._messageThreadView.removeFromSuperview()
    }

    configureResult(
        part: RHProcurementPart,
        partOffer: RHProcurementPartOffer,
        isEvaluationFinalized: boolean,
        hasWinner: boolean,
        procurementOfferId: string | undefined,
        partId: string,
        unreadMessageCount?: number,
        partCount?: number,
        isClosedWithoutContract: boolean = NO,
        isLockedForReconciliationWithoutCurrentCompany: boolean = NO,
        isContractCancelled: boolean = NO
    ) {
        // Shared top section (header + price + documents).
        this.configureWithPartOffer(part, partOffer, partCount)

        const footerViews: UIView[] = []

        if (isClosedWithoutContract) {
            this.decisionBanner.configureBannerWithObject({
                text: RHProcurementPartDisplay.closedWithoutContractStatusText(this.numberOfRootProcurementParts),
                alertType: RHAlertBannerView.type.Warning,
                textPresentation: RHAlertBannerView.textPresentation.Outcome
            })
            footerViews.push(this.decisionBanner)
        }
        else if (isContractCancelled) {
            let contractCancelledText = "Hange on lõpetatud ilma lepinguta. Varasem edukaks tunnistamise otsus ei vii lepinguni."
            if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this.numberOfRootProcurementParts)) {
                contractCancelledText = "Hankeosa on lõpetatud ilma lepinguta. Varasem edukaks tunnistamise otsus ei vii lepinguni."
            }
            this.decisionBanner.configureBannerWithObject({
                text: contractCancelledText,
                alertType: RHAlertBannerView.type.Warning,
                textPresentation: RHAlertBannerView.textPresentation.Outcome
            })
            footerViews.push(this.decisionBanner)
        }
        else if (isEvaluationFinalized && !isClosedWithoutContract) {
            const isWinner = partOffer.decision === "chosenAsWinner"
            const meetsRequirements = partOffer.meetsRequirements

            if (isWinner) {
                let winnerText = "🏆 Õnnitleme! Sinu pakkumus tunnistati edukaks."
                if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this.numberOfRootProcurementParts)) {
                    winnerText = "🏆 Õnnitleme! Sinu pakkumus tunnistati selles hankeosas edukaks."
                }
                this.decisionBanner.configureBannerWithObject({
                    text: winnerText,
                    alertType: RHAlertBannerView.type.Success,
                    textPresentation: RHAlertBannerView.textPresentation.Outcome
                })
            }
            else if (!hasWinner) {
                let noWinnerText =
                    "Selles hankes ei tunnistatud ühtegi pakkumust edukaks – hange jääb lepinguta."
                if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this.numberOfRootProcurementParts)) {
                    noWinnerText =
                        "Selles hankeosas ei tunnistatud ühtegi pakkumust edukaks – hankeosa jääb lepinguta."
                }
                if (meetsRequirements === NO) {
                    noWinnerText += " Sinu pakkumus ei vastanud hanketingimustele."
                }
                this.decisionBanner.configureBannerWithObject({
                    text: noWinnerText,
                    alertType: RHAlertBannerView.type.Warning,
                    textPresentation: RHAlertBannerView.textPresentation.Outcome
                })
            }
            else if (meetsRequirements === NO) {
                this.decisionBanner.configureBannerWithObject({
                    text: "✗ Kahjuks ei vastanud sinu pakkumus hanketingimustele.",
                    alertType: RHAlertBannerView.type.Error,
                    textPresentation: RHAlertBannerView.textPresentation.Outcome
                })
            }
            else {
                let notWinnerText = "Sinu pakkumust ei tunnistatud edukaks."
                if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this.numberOfRootProcurementParts)) {
                    notWinnerText = "Sinu pakkumust ei tunnistatud selles hankeosas edukaks."
                }
                this.decisionBanner.configureBannerWithObject({
                    text: notWinnerText,
                    alertType: RHAlertBannerView.type.Warning,
                    textPresentation: RHAlertBannerView.textPresentation.Outcome
                })
            }
            footerViews.push(this.decisionBanner)
        }
        else if (isLockedForReconciliationWithoutCurrentCompany && !isClosedWithoutContract) {
            this.reconciliationPendingBanner.configureBannerWithObject({
                text: "Hange on viidud järgmisesse vooru, millesse antud pakkumust ei kaasatud.",
                alertType: RHAlertBannerView.type.Warning,
                textPresentation: RHAlertBannerView.textPresentation.Outcome
            })
            if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this.numberOfRootProcurementParts)) {
                this.reconciliationPendingBanner.text =
                    "Hankeosa on viidud järgmisesse vooru, millesse antud pakkumust ei kaasatud."
            }
            footerViews.push(this.reconciliationPendingBanner)
        }
        if ((isEvaluationFinalized || isClosedWithoutContract || isContractCancelled) && partOffer.evaluationNotes) {
            this.evaluationNotesLabel.text =
                "Hindaja kommentaar: " + RHTextSanitizer.escapedHTML(partOffer.evaluationNotes)
            footerViews.push(this.evaluationNotesLabel)
        }

        this._procurementOfferId = procurementOfferId
        this._partId = partId
        this._clearPrivateMessagingUI()

        // The whole private-messaging block is gated behind the visibility flag:
        // hidden, not removed (the accordion is simply never added to the tree).
        if (RHFeatureFlags.offerMessagingIsVisible && procurementOfferId) {

            // Emphasise that this offer thread is private (1:1 offerer ↔ procurer),
            // unlike the public "Teabevahetus" Q&A.
            this._messagingAccordion.contentView = this._messageThreadView
            this._messagingAccordion.title = "<b>Privaatsed</b> sõnumid"
            const storeCount = procurementOfferId
                               ? RHCore.sharedInstance.notificationStore.offerMessageCount(procurementOfferId)
                               : 0
            RHNotificationBadge.applyUnreadEmphasis(
                this._messagingAccordion.headerButton.titleLabel,
                (storeCount > 0 || (unreadMessageCount ?? 0) > 0)
            )
            footerViews.push(this._messagingAccordion)

            // Re-register thread subscription now that we may have a new offer ID.
            if (this.superview) {
                RHCore.sharedInstance.offerStore.unsubscribeFromThread(this)
                if (procurementOfferId) {
                    RHCore.sharedInstance.offerStore.subscribeToThread(
                        this,
                        procurementOfferId,
                        () => {
                            RHNotificationBadge.applyUnreadEmphasis(
                                this._messagingAccordion.headerButton.titleLabel,
                                YES
                            )
                        }
                    )
                }
            }

            if (procurementOfferId) {
                // noinspection JSConstantReassignment
                this._messagingAccordion.headerButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
                    if (this._messagingAccordion._expanded) {
                        void this._messageThreadView.configure(procurementOfferId, "offerer", partId)
                    }
                }
            }

        }

        this.setFooterViews(footerViews)
    }

}


// ─────────────────────────────────────────────────────────────────────────────
// Main view
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shown to the offerer after the procurement deadline has passed.
 * Displays every submitted part offer as a separate white card, plus a status
 * banner that reflects the evaluation state.
 *
 * Status logic:
 *   - published  + deadline passed  → evaluation in progress
 *   - completed  + any winner       → show per-part win/loss results
 *   - cancelled                     → procurement was cancelled
 */
export class OfferResultView extends UIView {

    displayedPartId?: string
    isHistorical = NO
    hasNextRound = NO
    isDraft = NO
    shouldShowOnlyOfferedParts = NO

    readonly titleLabel = new UITextView()
        .configuredWithObject({
            text: "Minu pakkumus",
            backgroundColor: UIColor.transparentColor,
            style: {
                color: RHColors.textBootstrap.stringValue,
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeXXLarge,
                fontWeight: "500",
                lineHeight: RHFont.lineHeightHeading
            }
        })

    readonly statusBanner = new RHAlertBannerView()
    readonly noOfferLabel = new UITextView().configuredWithObject({
        text: "Pakkumust ei esitatud.",
        textColor: RHColors.textSecondary
    })
    readonly deadlineExplanationLabel = new UITextView().configuredWithObject({
        backgroundColor: UIColor.transparentColor,
        textColor: RHColor.danger,
        text: "Pakkumuste esitamise tähtaeg on möödas"
    })

    _partViews: UIView[] = []
    override _focusManager: RHFocusManager = new RHFocusManager(() =>
        this._partViews.filter(partView => partView._focusManager && partView.superview === this)
    )
    private _procurementOfferId?: string

    constructor(elementID?: string) {
        super(elementID)
    }

    private _resetStatusBannerChrome() {
        this.statusBanner.style.border = ""
        this.statusBanner.style.borderLeft = ""
        this.statusBanner.style.boxShadow = ""
    }

    private _configurePendingStatusBanner() {
        const tokens = RHMakeOfferViewTokens
        this.statusBanner.configureBannerWithObject({
            text: "Sinu pakkumus on esitatud. Hindamine ei ole veel lõppenud.",
            textPresentation: RHAlertBannerView.textPresentation.Outcome,
            backgroundColor: RHColors.warningAmberSurfaceDraft,
            borderColor: RHColors.warningAmberBorderDraft,
            textColor: RHColors.warningAmberTextDeep,
            fontSize: RHFont.sizeMedium,
            fontWeight: "600",
            fontFamily: RHFont.familyBase,
            padding: { top: 11, right: 14, bottom: 11, left: 14 }
        })
        this.statusBanner.style.border =
            `${1}px solid ${RHColors.warningAmberBorderDraft.stringValue}`
        this.statusBanner.style.borderLeft =
            `${4}px solid ${RHColors.warningAmber.stringValue}`
        this.statusBanner.style.borderRadius = `${RHRadius.large}px`
        this.statusBanner.style.boxShadow = "0 1px 3px rgba(245, 158, 11, 0.14)"
    }

    configure(
        procurement: RHProcurementPublic,
        submittedOffer: RHProcurementOffer | null,
        numberOfRootProcurementParts: number,
        closedOffers: RHProcurementOffer[] = []
    ) {
        // Tear down previous state
        this._partViews.forEach(partView => partView.removeFromSuperview())
        this._partViews = []
        this._procurementOfferId = submittedOffer?._id?.toString()

        const status = procurement.status
        const allPartsEvaluationFinalized = status !== "published" && status !== "closed-without-contract"
        const finalizedPartIds: string[] = procurement.finalizedPartIds ?? []
        const closedWithoutContractPartIds = new Set(
            (procurement.closedWithoutContractPartIds ?? []).map(partId => partId.toString())
        )
        const offers = [...closedOffers]
        if (submittedOffer) {
            offers.unshift(submittedOffer)
        }
        const offersByPartOffer = RHProcurementPartOfferGrouping.offersByPartOffer(offers)
        const partOffersByPartId = RHProcurementPartOfferGrouping.partOffersByPartId([
            ...offersByPartOffer.keys()
        ])
        // Locked-for-reconciliation parts this company was NOT invited into —
        // see RHProcurementPublic.liveReconciliationRoundProcurementIdForCurrentCompany.
        // A part in reconciliationLockedPartIds but absent from that map is
        // being decided in a round this offerer isn't part of.
        const partIdsWithLiveRoundForCurrentCompany = new Set(
            Object.keys(procurement.liveReconciliationRoundProcurementIdForCurrentCompany ?? {})
        )
        const lockedPartIdsWithoutCurrentCompany = new Set(
            (procurement.reconciliationLockedPartIds ?? [])
                .map(partId => partId.toString())
                .filter(partId => procurement.liveReconciliationRoundProcurementIdForCurrentCompany !== undefined &&
                    !partIdsWithLiveRoundForCurrentCompany.has(partId))
        )
        const hasPendingOfferedParts = !allPartsEvaluationFinalized && [...partOffersByPartId].some(([partId, partOffers]) =>
            (!this.displayedPartId || partId === this.displayedPartId) &&
            partOffers.some(partOffer => !partOffer.isCancelled) &&
            !closedWithoutContractPartIds.has(partId) && !finalizedPartIds.includes(partId) &&
            !lockedPartIdsWithoutCurrentCompany.has(partId)
        )

        // ── Status banner ─────────────────────────────────────────────────────

        if (this.hasNextRound) {
            this._resetStatusBannerChrome()
            this.statusBanner.configureBannerWithObject({
                text: RHProcurementPartDisplay.evaluationMovedToNextRoundText,
                alertType: RHAlertBannerView.type.Warning,
                textPresentation: RHAlertBannerView.textPresentation.Outcome
            })
        }
        else if (status === "closed-without-contract") {
            this._resetStatusBannerChrome()
            this.statusBanner.configureBannerWithObject({
                text: RHProcurementPartDisplay.closedWithoutContractStatusText(numberOfRootProcurementParts),
                alertType: RHAlertBannerView.type.Warning,
                textPresentation: RHAlertBannerView.textPresentation.Outcome
            })
        }
        else if (hasPendingOfferedParts) {
            this._configurePendingStatusBanner()
        }
        // When evaluation is finalized the top-level aggregate banner is intentionally
        // omitted: each part card shows a single consolidated decision banner instead.

        // ── Part cards ────────────────────────────────────────────────────────

        const partCount = procurement.parts.length
        for (const part of procurement.parts) {
            const partIdString = part._id.toString()
            if (this.displayedPartId && partIdString !== this.displayedPartId) {
                continue
            }
            const offersForPart = partOffersByPartId.get(partIdString) ?? []
            if (this.shouldShowOnlyOfferedParts && offersForPart.length === 0) {
                continue
            }
            const isClosedWithoutContract = status === "closed-without-contract" ||
                closedWithoutContractPartIds.has(partIdString)
            if (isClosedWithoutContract && offersForPart.length === 0) {
                const closedPartView = new RHProcurementPartView()
                closedPartView.configureWithPartData(part, YES, undefined, partCount, YES, numberOfRootProcurementParts)
                this._partViews.push(closedPartView)
            }
            const partHasWinner =
                !!(procurement.partWinnerOfferIds ?? {})[partIdString] ||
                offersForPart.some(partOffer => partOffer.decision === "chosenAsWinner")
            for (const [partOfferIndex, partOffer] of offersForPart.entries()) {
                const submittedOfferForPart = offersByPartOffer.get(partOffer)
                const hasLaterAlternativeInOffer = offersForPart.slice(partOfferIndex + 1).some(
                    otherPartOffer => offersByPartOffer.get(otherPartOffer) === submittedOfferForPart
                )
                let procurementOfferIdForMessaging: string | undefined
                if (!this.isDraft && !this.isHistorical && !hasLaterAlternativeInOffer) {
                    procurementOfferIdForMessaging = submittedOfferForPart?._id?.toString()
                }

                // A part's evaluation is finalized either when the whole procurement is
                // finalized, or when this specific part appears in finalizedPartIds.
                const partEvaluationFinalized =
                    !this.isDraft && !this.hasNextRound && (allPartsEvaluationFinalized || finalizedPartIds.includes(partIdString))
                const card = new OfferPartResultCard(numberOfRootProcurementParts)
                card.configureResult(
                    part,
                    partOffer,
                    partEvaluationFinalized,
                    partHasWinner,
                    procurementOfferIdForMessaging,
                    partIdString,
                    undefined,
                    partCount,
                    isClosedWithoutContract,
                    !this.isDraft && lockedPartIdsWithoutCurrentCompany.has(partIdString),
                    !this.isDraft && (procurement.cancelledContractPartIds ?? []).includes(partIdString)
                )
                if (isClosedWithoutContract && submittedOfferForPart?.submittedAt) {
                    card.decisionBanner.text += "<br>" + OfferSubmittedView._detailsHTMLForOffer(submittedOfferForPart)
                }
                this._partViews.push(card)
            }
        }

        // ── Add subviews ──────────────────────────────────────────────────────

        this.titleLabel.addedAsSubviewToView(this)
        this.noOfferLabel.removeFromSuperview()
        if (!submittedOffer && closedOffers.length === 0) {
            this.noOfferLabel.addedAsSubviewToView(this)
        }
        if (this.hasNextRound || status === "closed-without-contract" || hasPendingOfferedParts) {
            this.statusBanner.addedAsSubviewToView(this)
        }
        else {
            this.statusBanner.removeFromSuperview()
        }
        if (!this.hasNextRound && (this.isHistorical || this.isDraft || this.shouldShowOnlyOfferedParts)) {
            this.statusBanner.removeFromSuperview()
        }
        if (this.isDraft) {
            this.titleLabel.text = "Ettevalmistamisel"
        }
        else {
            this.titleLabel.text = "Minu pakkumus"
        }
        if (this.isDraft && procurement.deadlineDate && new Date(procurement.deadlineDate).getTime() <= Date.now()) {
            this.deadlineExplanationLabel.addedAsSubviewToView(this)
        }
        else {
            this.deadlineExplanationLabel.removeFromSuperview()
        }
        this._partViews.forEach(partView => partView.addedAsSubviewToView(this))

        this.setNeedsLayoutUpToRootView()
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(0)

        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .IF(this.noOfferLabel.superview === this)
            .rectangleForNextRow(padding * 0.5, this.noOfferLabel)
            .assignedAsFrameOfView(this.noOfferLabel)
            .ENDIF()
            .IF(this.deadlineExplanationLabel.superview === this)
            .rectangleForNextRow(padding * 0.5, this.deadlineExplanationLabel)
            .assignedAsFrameOfView(this.deadlineExplanationLabel)
            .ENDIF()
            .IF(this.statusBanner.superview === this)
            .rectangleForNextRow(padding, this.statusBanner)
            .assignedAsFrameOfView(this.statusBanner)
            .ENDIF()
            .IF(this._partViews.length > 0)
            .rectangleForNextRow(padding * 1.5, 0)
            .framesByDistributingViewsAsColumn(this._partViews, padding * 1.5, this._partViews)
            .ENDIF()
    }

    override intrinsicContentHeight(constrainingWidth = 0): number {
        const padding = this.core.paddingLength
        const width = constrainingWidth || this.bounds.width

        let height = this.titleLabel.intrinsicContentHeight(width)
        if (this.noOfferLabel.superview === this) {
            height += padding * 0.5 + this.noOfferLabel.intrinsicContentHeight(width)
        }
        if (this.deadlineExplanationLabel.superview === this) {
            height += padding * 0.5 + this.deadlineExplanationLabel.intrinsicContentHeight(width)
        }

        if (this.statusBanner.superview === this) {
            height += padding + this.statusBanner.intrinsicContentHeight(width)
        }

        this._partViews.forEach(partView => {
            height += padding * 1.5 + partView.intrinsicContentHeight(width)
        })

        return height
    }

}
